import React from "react";
import { Card, Typography, Button, InputNumber, Image } from "antd";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useUser } from "../context/UserContext";
import { Link } from "react-router-dom";
import { calcularMontoArticulo } from "../utils/pedidos/montos";
import { DeleteOutlined, PlusOutlined, MinusOutlined, ShoppingOutlined } from "@ant-design/icons";

const { Text } = Typography;

const CartItem = ({ item }) => {
  const { userData } = useUser();

  const updateCartQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    if (item.stock && newQuantity > item.stock) newQuantity = item.stock;
    try {
      const userCartRef = doc(db, "carritos", userData.uid);
      const userCartDoc = await getDoc(userCartRef);

      if (userCartDoc.exists()) {
        const updatedProducts = userCartDoc.data().productos.map((product) => {
          if (product.id === productId) {
            return { ...product, cantidad: newQuantity };
          }
          return product;
        });
        await updateDoc(userCartRef, { productos: updatedProducts });
      }
    } catch (error) {
      console.error("Error al actualizar cantidad:", error.message);
    }
  };

  const handleRemoveFromCart = async () => {
    try {
      const userCartRef = doc(db, "carritos", userData.uid);
      const userCartDoc = await getDoc(userCartRef);

      if (userCartDoc.exists()) {
        const updatedProducts = userCartDoc
          .data()
          .productos.filter((product) => product.id !== item.id);
        await updateDoc(userCartRef, { productos: updatedProducts });
      }
    } catch (error) {
      console.error("Error al eliminar producto:", error.message);
    }
  };

  const handleRemoveFromCartService = async () => {
    try {
      const userCartRef = doc(db, "carritos", userData.uid);
      const userCartDoc = await getDoc(userCartRef);

      if (userCartDoc.exists()) {
        const updatedServices = userCartDoc
          .data()
          .servicios.filter((service) => service.id !== item.id);
        await updateDoc(userCartRef, { servicios: updatedServices });
      }
    } catch (error) {
      console.error("Error al eliminar servicio:", error.message);
    }
  };

  const handleQuantityChange = async (newQuantity) => {
    updateCartQuantity(item.id, newQuantity);
  };

  const getSubtotal = () => {
    if (item.tipo === "servicio") {
      return calcularMontoArticulo(item);
    }
    return item.total * (item.cantidad || 1);
  };

  const getImageUrl = () => {
    if (item.imagen && item.imagen.length > 0) {
      const first = item.imagen[0];
      return typeof first === "string" ? first : (first?.url || "");
    }
    if (typeof item.imagen === "string") return item.imagen;
    if (item.foto) return item.foto;
    if (item.imagenes && item.imagenes.length > 0) {
      const first = item.imagenes[0];
      return typeof first === "string" ? first : (first?.url || "");
    }
    if (item.fotos && item.fotos.length > 0) {
      const first = item.fotos[0];
      return typeof first === "string" ? first : (first?.url || "");
    }
    return "";
  };

  const imageUrl = getImageUrl();

  if (item.tipo === "servicio") {
    return (
      <Card className="cart-item-card service-item" size="small" style={item.noDisponible ? { opacity: 0.6 } : undefined}>
        {item.noDisponible && <Text type="danger" strong>{item.motivoNoDisponible}. Elimínalo para continuar.</Text>}
        <div className="item-content">
          <div className="item-image-wrapper">
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={item.nombre}
                className="item-image"
                preview={false}
              />
            ) : (
              <div className="item-image-placeholder">
                <ShoppingOutlined style={{ fontSize: 24, color: "#9ca3af" }} />
                <span style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>Sin imagen</span>
              </div>
            )}
          </div>
          <div className="item-details">
            <Link to={`/servicio/${item.id}`} className="item-name-link">
              <Text strong className="item-name">{item.nombre}</Text>
            </Link>
            <Text type="secondary" className="item-price">
              ${parseFloat(item.total).toFixed(2)} MXN
            </Text>
            <div className="item-meta">
              <Text type="secondary" className="meta-text">
                {item.horaInicio} - {item.horaFin}
              </Text>
              <Text type="secondary" className="meta-text">
                Duración: {item.duracion}
              </Text>
            </div>
            {item.horaInicioSeleccionada && (
              <Text type="secondary" className="meta-text">
                Hora: {item.horaInicioSeleccionada} | Día: {item.diaSeleccionado}
              </Text>
            )}
            {item.cantidadDuracion && (
              <Text type="secondary" className="meta-text">
                Cantidad: {item.cantidadDuracion}
              </Text>
            )}
            <div className="item-footer">
              <Text strong className="item-subtotal">
                ${getSubtotal().toFixed(2)}
              </Text>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={handleRemoveFromCartService}
                className="remove-btn"
                size="small"
              >
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="cart-item-card product-item" size="small" style={item.noDisponible ? { opacity: 0.6 } : undefined}>
      {item.noDisponible && <Text type="danger" strong>{item.motivoNoDisponible}. Elimínalo para continuar.</Text>}
      <div className="item-content">
        <div className="item-image-wrapper">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={item.nombre}
              className="item-image"
              preview={false}
            />
          ) : (
            <div className="item-image-placeholder">
              <ShoppingOutlined style={{ fontSize: 24, color: "#9ca3af" }} />
              <span style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>Sin imagen</span>
            </div>
          )}
        </div>
        <div className="item-details">
          <Link to={`/producto/${item.id}`} className="item-name-link">
            <Text strong className="item-name">{item.nombre}</Text>
          </Link>
          <Text type="secondary" className="item-price">
            ${parseFloat(item.total).toFixed(2)} MXN
          </Text>
          <div className="quantity-control">
            <Button
              type="text"
              icon={<MinusOutlined />}
              onClick={() => handleQuantityChange((item.cantidad || 1) - 1)}
              disabled={item.cantidad <= 1}
              size="small"
              className="qty-btn"
            />
            <InputNumber
              min={1}
              max={item.stock}
              value={item.cantidad || 1}
              onChange={handleQuantityChange}
              size="small"
              className="qty-input"
            />
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={() => handleQuantityChange((item.cantidad || 1) + 1)}
              size="small"
              className="qty-btn"
            />
          </div>
          <div className="item-footer">
            <Text strong className="item-subtotal">
              ${getSubtotal().toFixed(2)} MXN
            </Text>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemoveFromCart}
              className="remove-btn"
              size="small"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default CartItem;